import { FileActionButton } from "../file-action/file-action-controls";
import AppLoading from "../app-loading/app-loading";
import React, { useState, useRef, useEffect } from "react";
import { Toast } from 'primereact/toast';
import CanceledOrderSearch from "./canceled-order-search";
import CanceledOrderDatatable from "./canceled-order-datatable";
import { CancelledOrders } from "../../models/orders/order-list-model";
import myOrderServices from "../../services/orders/myorder.service";
import { DataTableStateEvent } from "primereact/datatable";
import { CancelledorderExport } from "./canceled-order-export";
 
const CanceledOrderMain: React.FC = () => {
    const toast = useRef<Toast>(null);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [dataList, setDataList] = useState<CancelledOrders[]>([]);
    const [hasSearched, setHasSearched] = useState(false); // 🆕
 
    const currentFilters = useRef<{
        phoneNumber?: string;
        startDate?: Date;
        endDate?: Date;
        pageNumber: number;
        pageSize: number;
    }>({
        pageNumber: 1,
        pageSize: 10
    });
 
    useEffect(() => {
        // On load, show empty state
        setHasSearched(false); // 🆕
        setDataList([]);
        setTotalRecords(0);
    }, []);
 
    const fetchCanceledOrders = async () => {
        setLoading(true);
        try {
            const response = await myOrderServices.GetCancelledOrders(currentFilters.current);
            setDataList(response.data || []);
            setTotalRecords(response.totalRecords || 0);
        } catch (error) {
            console.error("Error fetching cancelled orders:", error);
            setDataList([]);
            setTotalRecords(0);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch cancelled orders',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };
 
    const handleSearch = async (filters: {
        phoneNumber?: string;
        startDate?: Date;
        endDate?: Date;
    }) => {
        currentFilters.current = {
            ...currentFilters.current,
            ...filters,
            pageNumber: 1
        };
        setPageNumber(1);
        await fetchCanceledOrders();
        setHasSearched(true); // 🆕
    };
 
    const handleReset = async () => {
        currentFilters.current = {
            phoneNumber: undefined,
            startDate: undefined,
            endDate: undefined,
            pageNumber: 1,
            pageSize: pageSize
        };
        setPageNumber(1);
        setDataList([]); // 🆕 clear data
        setTotalRecords(0); // 🆕 clear count
        setHasSearched(false); // 🆕 show message again
    };
 
    const handlePageChange = (event: DataTableStateEvent) => {
        const newPageNumber = Math.floor((event.first || 0) / (event.rows || 10)) + 1;
        const newPageSize = event.rows || 10;
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
 
        currentFilters.current = {
            ...currentFilters.current,
            pageNumber: newPageNumber,
            pageSize: newPageSize
        };
       
        fetchCanceledOrders();
    };
 
    return (
        <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
            <Toast ref={toast} />
            <div className="col-12">                
                <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full card-container mb-0">
                    <h5 className="ml-1 mb-0">Cancelled Orders</h5>
                    <span>
                        <FileActionButton action="export"
                            className="text-black border-round mr-2"
                            onClick={() => CancelledorderExport(dataList)}
                            icon="pi pi-file-export mr-1"
                            text raised
                            disabled={!hasSearched || dataList.length === 0} // 🆕 Disable if not searched
                        >
                            Export Cancelled orders
                        </FileActionButton>
                    </span>            
                </div>                
            </div>                  
 
            <CanceledOrderSearch
                onSearch={handleSearch}
                onReset={handleReset}
                defaultStartDate={undefined} // 🆕 Show empty fields
                defaultEndDate={undefined}
            />
 
            {loading && <AppLoading />}
 
            {!loading && hasSearched ? (
                <CanceledOrderDatatable
                    data={dataList}
                    pageNumber={pageNumber}
                    pageSize={pageSize}
                    NoofRecords={totalRecords}
                    onPageChange={handlePageChange}
                />
            ) : !loading && !hasSearched ? (
                <div className="col-12 text-center p-4">
                    <p>Please apply filters to view cancelled orders</p>
                </div>
            ) : null}
        </div>
    );
};
 
export default CanceledOrderMain;